import { type DeliveryAddress } from '@koala/sdk';
import { nanoid } from 'nanoid';
import { useState, useEffect } from 'react';
import { type ConnectedProps, connect } from 'react-redux';
import { autocompleteInputId } from './autocomplete-input-id';
import {
  StyledAddShippingDetails,
  StyledAddShippingDetailsLabel,
  StyledAutocompleteInstructions,
  StyledExclamationCircle,
  StyledInputResetButton,
} from './styles';
import { addressValidation } from './validation';
import StringAccessor from '@/components/cmsConfig/stringAccessor';
import { SecureField } from '@/components/uielements/form-fields';
import { StyledLabel } from '@/components/uielements/label';
import { StyledSearchInput } from '@/components/uielements/searchInput';
import { DELIVERY_TIME_WANTED_MODES } from '@/constants/global';
import { SUPPORTED_COUNTRIES } from '@/constants/locations';
import { StyledLoader } from '@/features/handoff/time-picker/styles';
import { locationsActions } from '@/redux/locations/actions';
import { type RootState } from '@/types/app';
import * as ErrorReporter from '@/utils/errorReporter';
import { useGoogleMapsLoader } from '@/utils/googleMapsLoader';
import { safelyGetString } from '@/utils/stringHelpers';

interface Props extends ReduxProps {
  apiKey: string;
  loading: boolean;
  checkOrSearchDeliveryCoverage: (address: DeliveryAddress) => void;
  formValues?: Record<string, string>;
}

const GoogleAutocomplete = (props: Props) => {
  // We need to generate unique input id for each GoogleAutocomplete component
  // as there are cases where we could have more than one instance of GoogleAutocomplete component:
  // Delivery Search on Locators page and Opened Delivery Form Modal
  const [generatedInputId] = useState<string>(`${autocompleteInputId}-${nanoid()}`);
  const [address, setAddress] = useState<DeliveryAddress | null>(null);
  const [, setSearchValue] = useState<string>('');
  const [missingAddressFields, setMissingAddressFields] = useState<string[]>([]);

  // Key + libraries come from the shared loader so this autocomplete shares the single app-wide
  // Google Maps load instead of triggering a second one with the CMS-fallback key (TEAM-13351).
  // (The `apiKey` prop is retained for the component contract but no longer selects the key.)
  const { isLoaded, loadError } = useGoogleMapsLoader();

  useEffect(() => {
    props.clearDeliverySearchErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loadError) {
      ErrorReporter.captureException(loadError);
      console.error(loadError);
      return;
    }
    if (isLoaded) {
      initAutocomplete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, loadError]);

  let autocomplete: google.maps.places.Autocomplete | null = null;

  const initAutocomplete = () => {
    if (autocomplete) {
      // Session already started
      return;
    }

    const input = document.getElementById(generatedInputId) as HTMLInputElement;

    autocomplete = new window.google.maps.places.Autocomplete(input, {
      types: ['geocode'],
      fields: ['address_components', 'place_id', 'name', 'types'],
      componentRestrictions: {
        country: Object.values(SUPPORTED_COUNTRIES),
      },
    });
    autocomplete.addListener('place_changed', storeSelectedAddress);

    // This disables 'enter' form submissions when the autocomplete input is in focus
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
      }
    });
  };

  const mapAutocompleteAddress = (selectedLocation?: google.maps.places.PlaceResult) => {
    const addressComponents = selectedLocation?.address_components;

    if (!addressComponents) {
      // Throw error if address is not recognized by Google
      props.showGoogleAddressNotSelectedError();
      return;
    }

    // Mapping of GeocoderAddressComponent.types[] to GoogleAddressComponentFields
    // All types represented as string and can be found
    // https://developers.google.com/maps/documentation/javascript/geocoding#GeocodingAddressTypes
    const newFormattedAddress: Record<string, string> = {};

    addressComponents.forEach((component) => {
      // Process each type for this component
      component.types.forEach((type) => {
        newFormattedAddress[type] = component.short_name;
      });
    });

    return newFormattedAddress;
  };

  // Set the selection to the Google autocomplete provided address
  const storeSelectedAddress = () => {
    const { clearDeliverySearchErrors, showGoogleAddressNotSelectedError, formValues } = props;

    // Clear any previous errors
    clearDeliverySearchErrors();

    const selectedLocation = autocomplete?.getPlace();
    const addressComponents = mapAutocompleteAddress(selectedLocation);

    if (!addressComponents) {
      // Throw error if address is not recognized by Google
      showGoogleAddressNotSelectedError();
      return;
    }

    // Parse street address
    const { street_number, route, subpremise } = addressComponents;
    const streetAddress = `${street_number ?? ''} ${route ?? ''}`.trim();

    // Set address in the format we want on state
    setAddress({
      time_wanted: formValues?.time_wanted ?? '',
      time_wanted_mode: formValues?.time_wanted
        ? DELIVERY_TIME_WANTED_MODES.ADVANCE
        : DELIVERY_TIME_WANTED_MODES.ASAP,
      street_address: streetAddress,
      street_address_2: subpremise ?? '',
      city:
        addressComponents.locality ??
        addressComponents.sublocality ??
        addressComponents.sublocality_level_1 ??
        addressComponents.administrative_area_level_3 ??
        '',
      state: addressComponents.administrative_area_level_1,
      zip_code: addressComponents.postal_code ?? '',
    });
  };

  useEffect(() => {
    if (address) {
      handleSubmitAddress(address);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  const handleSubmitAddress = (address: DeliveryAddress) => {
    const { showDeliveryAddressInvalidError, checkOrSearchDeliveryCoverage } = props;

    const validation = address && addressValidation(address);

    // Throw error if missing required address fields
    if (Object.keys(validation).length > 0) {
      setMissingAddressFields(Object.keys(validation));
      showDeliveryAddressInvalidError();
      return;
    }

    // This has been extended to accept both API calls, depending on where it's used
    checkOrSearchDeliveryCoverage(address);
  };

  const clearInputField = () => {
    const element = document.getElementById(generatedInputId) as HTMLInputElement;
    element.value = '';
    props.clearDeliverySearchErrors();
    setSearchValue('');
  };

  const renderInputError = () => {
    const { invalidDeliverySearchAddress, googleAddressNotSelected } = props;
    const formattedMissingAddressFields = missingAddressFields.join(', ').replace(/_/g, ' ');

    // User enters incomplete address, e.g. Brooklyn, NY
    if (invalidDeliverySearchAddress) {
      return (
        missingAddressFields.length > 0 && (
          <StringAccessor
            accessor="locations.incomplete_address_text"
            html={true}
            dataObj={{ missingAddressFields: formattedMissingAddressFields }}
          />
        )
      );
    }

    // User presses enter without selecting an address from the dropdown
    if (googleAddressNotSelected) {
      return <StringAccessor accessor="locations.google_address_not_selected_text" html={true} />;
    }
  };

  const {
    strings,
    loading,
    invalidDeliverySearchAddress,
    noDeliveryLocationsFound,
    googleAddressNotSelected,
  } = props;

  const inputId = 'delivery-address-autocomplete-instructions';

  return (
    <>
      <div>
        <StyledLabel as="span" id="autocomplete-instructions" $visuallyHidden={true}>
          Start typing your address, key down when you find your address, and then press enter
        </StyledLabel>
        <SecureField
          fieldType={inputId}
          label="Delivery Address"
          error={renderInputError()}
          aria-describedby={inputId}
        >
          <StyledSearchInput
            id={generatedInputId}
            placeholder={safelyGetString(strings, 'locations.delivery_search_placeholder')}
            $padding="0 25px 0 10px"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
          />
          {loading && !noDeliveryLocationsFound && <StyledLoader $right={20} $top={10} />}

          {(noDeliveryLocationsFound ||
            invalidDeliverySearchAddress ||
            googleAddressNotSelected) && (
            <StyledInputResetButton aria-label="Clear address" onClick={clearInputField}>
              &times;
            </StyledInputResetButton>
          )}
        </SecureField>
      </div>

      {/* No Delivery locations returned from API  */}
      {noDeliveryLocationsFound && !invalidDeliverySearchAddress && !loading && (
        <StringAccessor
          tag={StyledAutocompleteInstructions}
          accessor="locations.delivery_search_no_results_text"
          html={true}
        />
      )}

      {/* Delivery Search Instructional Text */}
      {!noDeliveryLocationsFound && !invalidDeliverySearchAddress && !googleAddressNotSelected && (
        <StyledAddShippingDetails>
          <StyledExclamationCircle title="Exclamation Circle" />

          <StyledAddShippingDetailsLabel>
            Add building, suite, floor, or apartment details in <b>Delivery Instructions</b> at
            checkout
          </StyledAddShippingDetailsLabel>
        </StyledAddShippingDetails>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  strings: state.app.cmsConfig.strings,
  invalidDeliverySearchAddress: state.app.locations.invalidDeliverySearchAddress,
  noDeliveryLocationsFound: state.app.locations.noDeliveryLocationsFound,
  googleAddressNotSelected: state.app.locations.googleAddressNotSelected,
});

const mapDispatchToProps = {
  showDeliveryAddressInvalidError: locationsActions.showDeliveryAddressInvalidError,
  showGoogleAddressNotSelectedError: locationsActions.showGoogleAddressNotSelectedError,
  clearDeliverySearchErrors: locationsActions.clearDeliverySearchErrors,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;
export default connector(GoogleAutocomplete);
